import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  FormGroup,
  Input,
  Label,
  Table,
} from "reactstrap";
import Select from "react-select";
import { resourcesBranchGeo, resourcesZones } from "../../../helpers/backend_helper";
import { getStorageUser, getStorageCompany } from "../../../helpers/api_helper";
import {
  Alert,
  ToastHelper,
  DestroyAlertHelper,
} from "../../../helpers/alerts/alertHelper";
import DataTableOnlyHelper from "../../../helpers/tables/DataTableOnlyHelper";
import AddGeoModal from "./AddGeoModal";

/*
  Una zona solo puede ocupar geocercas contiguas. El ramal se parte en dos tramos que
  comparten la geocerca intermedia (ida: inicio..intermedia, regreso: intermedia..fin);
  al pasar la intermedia una zona puede volver a aparecer. Las geocercas sin zona no
  cuentan. Debe coincidir con validarZonasContiguas() de BranchGeofencesController.
*/
const validarZonas = (items) => {
  const base = items.findIndex((item) => item.is_base == true);
  const tramos = {
    ida: items.map((_, idx) => idx).slice(0, base + 1),
    regreso: items.map((_, idx) => idx).slice(base),
  };

  for (const [tramo, indices] of Object.entries(tramos)) {
    const cerradas = [];
    let actual = null;
    for (const idx of indices) {
      const zona = items[idx].zone_id ? Number(items[idx].zone_id) : null;
      if (!zona || zona === actual) continue;
      if (cerradas.includes(zona)) return { idx, zona, tramo };
      if (actual) cerradas.push(actual);
      actual = zona;
    }
  }
  return null;
};

const GeofenceModal = ({ modal, setModal }) => {
  const company = getStorageCompany();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(false);
  const [errors, setErrors] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState(null);
  const [zones, setZones] = useState([]);
  const [errorRow, setErrorRow] = useState(null);
  const [item, setItem] = useState({
    id: null,
    geofence_id: null,
    zone_id: null,
    branch_id: modal.id,
    position: null,
    is_base: false,
    timeOne: 0,
    timeTow: 0,
    timeThree: 0,
    timeFour: 0,
    timeFive: 0,
    timeSix: 0,
    timeSeven: 0,
    timeEight: 0,
    timeNine: 0,
    tolerancy: 0,
  });
  const [newGeo, setNewGeo] = useState({ show: false, update: false });
  const authUser = getStorageUser();
  const handleClose = () => {
    setModal({ ...modal, show: false, id: null });
  };

  const destroy = (id) => {
    DestroyAlertHelper(async (confirmed) => {
      if (!confirmed) {
        return;
      }

      setItems(items.filter((i) => i.id != id));
      setEdit(true);
    });
  };

  useEffect(() => {
    const showBranchGeo = async () => {
      const response = await resourcesBranchGeo("show", {}, modal.id);
      // Sin zona guardada en el ramal se propone la zona de la geocerca
      setItems(
        response.data.map((geo) => ({
          ...geo,
          zone_id: geo.zone_id ?? geo.geofence_zone_id ?? null,
        }))
      );
    };
    showBranchGeo();
    resourcesZones("get", { params: { select: true, route_id: modal.route_id } }).then(
      (response) => setZones(response.data ?? [])
    );
  }, []);

  const timers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const dragItem = useRef();
  const dragOverItem = useRef();
  const dragStart = (e, position) => {
    dragItem.current = position;
  };
  const dragEnter = (e, position) => {
    dragOverItem.current = position;
  };

  const drop = (e, geo) => {
    let position = dragOverItem.current + 1;
    const copyListItems = [...items];
    const dragItemContent = copyListItems[dragItem.current];
    copyListItems.splice(dragItem.current, 1);
    copyListItems.splice(dragOverItem.current, 0, dragItemContent);
    dragItem.current = null;
    dragOverItem.current = null;
    setItems(copyListItems);
    setEdit(true);
  };

  const handlerOnChange = (e, geo) => {
    const newItem = geo;
    newItem[e.target.name] = e.target.value;
    let array = items.map((item) => (item.id !== newItem.id ? item : newItem));
    setItems(array);
    setEdit(true);
  };

  const changeIsBase = (row) => {
    const newItem = row;
    let array = items.map((item) => {
      item.is_base = (item.id == newItem.id);
      return item;
    });
    setItems(array);
    setEdit(true);
  };

  const save = async () => {
    if (validate()) {
      const response = await resourcesBranchGeo("post", items);
      if (response.status === false) {
        setErrorGeneral(response.message);
        return;
      }
      setEdit(false);
    }
  };

  const validate = () => {
    let errorsNo = 0;
    let base = items.filter((item) => item.is_base == true);
    errorsNo = base.length == 0 ? errorsNo + 1 : errorsNo;
    errorsNo = items.length == 0 ? errorsNo + 1 : errorsNo;
    
    if (errorsNo > 0) {
      setErrorRow(null);
      setErrorGeneral('Debe agregar almenos 1 geocerca e indicar una geocerca intermedia.')
      return false;
    }

    const errorZona = validarZonas(items);
    if (errorZona) {
      const geo = items[errorZona.idx];
      const zona = zones.find((z) => z.id == errorZona.zona);
      setErrorRow(errorZona.idx);
      setErrorGeneral(
        `La zona ${zona?.name ?? ""} se repite en «${geo?.alias?.length > 0 ? geo.alias : geo.geofence_name}» ` +
        `(posición ${errorZona.idx + 1}) sin ser contigua en el tramo de ${errorZona.tramo}. ` +
        `Una zona solo puede repetirse en geocercas contiguas.`
      );
      return false;
    }

    setErrorRow(null);
    setErrorGeneral(null)
    return true;
  };

  return (
    <div>
      <Modal
        isOpen={modal.show}
        className="modal-dialog modal-xl"
        role="document"
      >
        <ModalHeader className="bg-primary">{"Geocercas"}</ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-sm-12 mb-2 text-end">
              <button
                className="btn btn-sm btn-primary pull-right"
                onClick={() => setNewGeo({ ...newGeo, position: items.length, show: true })}
              >
                <i className="fas fa-solid fa-plus"></i> Agregar Geocerca
              </button>
            </div>

            <div className="col-sm-12">
            <Table hover responsive size="sm" className="align-middle">
              <thead>
                <tr>
                  <th></th>
                  <th>Intermedia</th>
                  <th>Zona</th>
                  <th>Geocerca</th>
                  {timers.map((time) => (
                    <th className="text-center" key={time}><small>Tiempo {time}</small></th>
                  ))}
                  <th>Tolerancia</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((geo, index) => (
                  <tr
                    style={{ textAlign: "center" }}
                    className={errorRow === index ? "table-danger" : ""}
                    key={index}
                    onDragEnd={(e) => drop(e, geo)}
                    onDragEnter={(e) => dragEnter(e, index)}
                    onDragStart={(e) => dragStart(e, index)}
                    draggable
                  >
                    <td>
                      <i
                        style={{ cursor: "move" }}
                        className="fa fa-bars"
                        aria-hidden="true"
                      ></i>
                    </td>
                    <td className="text-center">
                      <FormGroup>
                        <Input
                          name="base"
                          type="checkbox"
                          onChange={(e) => changeIsBase(geo)}
                          checked={geo.is_base}
                        />
                      </FormGroup>
                    </td>
                    <td>
                      <select
                        className="form-select"
                        name="zone_id"
                        style={{ minWidth: 130, borderLeft: `6px solid ${zones.find((z) => z.id == geo.zone_id)?.color || "#ced4da"}` }}
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.zone_id ?? ""}
                      >
                        <option value="">Sin zona</option>
                        {zones.map((zone) => (
                          <option key={zone.id} value={zone.id}>{zone.name}</option>
                        ))}
                      </select>
                    </td>
                    <td>{geo?.alias?.length > 0 ? geo?.alias : geo.geofence_name}</td>
                    <td>
                      <input
                        className="form-control"
                        style={{ minWidth: 56 }}
                        placeholder={"T1"}
                        name="timeOne"
                        type="text"
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.timeOne}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        style={{ minWidth: 56 }}
                        placeholder={"T2"}
                        name="timeTow"
                        type="text"
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.timeTow}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        style={{ minWidth: 56 }}
                        placeholder={"T3"}
                        name="timeThree"
                        type="text"
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.timeThree}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        style={{ minWidth: 56 }}
                        placeholder={"T4"}
                        name="timeFour"
                        type="text"
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.timeFour}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        style={{ minWidth: 56 }}
                        placeholder={"T5"}
                        name="timeFive"
                        type="text"
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.timeFive}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        style={{ minWidth: 56 }}
                        placeholder={"T6"}
                        name="timeSix"
                        type="text"
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.timeSix}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        style={{ minWidth: 56 }}
                        placeholder={"T7"}
                        name="timeSeven"
                        type="text"
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.timeSeven}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        style={{ minWidth: 56 }}
                        placeholder={"T8"}
                        name="timeEight"
                        type="text"
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.timeEight}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        style={{ minWidth: 56 }}
                        placeholder={"T9"}
                        name="timeNine"
                        type="text"
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.timeNine}
                      />
                    </td>
                    <td>
                      <input
                        className="form-control"
                        style={{ minWidth: 56 }}
                        name="tolerancy"
                        type="text"
                        onChange={(e) => handlerOnChange(e, geo)}
                        value={geo.tolerancy}
                      />
                    </td>
                    <td>
                      <div className="d-flex justify-content-between">
                        <div className="p-2">
                          <button
                            className="btn btn-primary btn-sm"
                            title="Editar ramal"
                            onClick={() => [
                              setItem(geo),
                              setNewGeo({
                                ...newGeo,
                                show: true,
                                update: true,
                                idx: index,
                              }),
                            ]}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        </div>
                        <div className="p-2">
                          <button
                            className="btn btn-danger btn-sm"
                            title="Eliminar ramal"
                            onClick={() => destroy(geo.id)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          </div>
          { errorGeneral && 
            <div className="alert alert-warning">{errorGeneral}</div>
          }
        </ModalBody>
        <ModalFooter>
          {edit && (
            <Button color="primary" onClick={save}>
              <i className="fa fa-save"></i> Guardar Cambios
            </Button>
          )}
          <Button color="secondary" onClick={handleClose}>
            <i className="fa fa-times"></i> Cerrar
          </Button>
        </ModalFooter>
      </Modal>
      {newGeo.show && (
        <AddGeoModal
          newGeo={newGeo}
          setNewGeo={setNewGeo}
          setItem={setItem}
          item={item}
          setItems={setItems}
          items={items}
          setEdit={setEdit}
        />
      )}
    </div>
  );
};

export default GeofenceModal;
